import type { ILanguage } from "@/types/languageTypes";
import { environment } from "@/environments/environment";
import microsoftLogoSvgRaw from "@/assets/microsoftLogo.svg?raw";

const pendingUhfPromises: Record<string, Promise<XMLDocument | null>> = {};
let assetsPromise: Promise<void> | undefined;

function createMicrosoftLogoSvg(): SVGSVGElement | null {
  const template = document.createElement("template");
  template.innerHTML = microsoftLogoSvgRaw.trim();

  const svg = template.content.querySelector("svg");
  return svg ? (svg.cloneNode(true) as SVGSVGElement) : null;
}

export function replaceUhfHeaderLogoInHeaderHtml(headerHtml: string): string {
  // UHF header is delivered as XML that contains a headerHtml string.
  // Only mutate the headerHtml snippet; do NOT mutate the XML payload.
  if (typeof document === "undefined") {
    return headerHtml;
  }

  const template = document.createElement("template");
  template.innerHTML = headerHtml;

  const imgs = template.content.querySelectorAll<HTMLImageElement>(
    "#uhfLogo > img, .uhf-microsoft-logo > img"
  );
  if (imgs.length === 0) {
    return headerHtml;
  }

  const svg = createMicrosoftLogoSvg();
  if (!svg) {
    return headerHtml;
  }

  imgs.forEach((img) => {
    const parent = img.parentElement;
    const svgNode = svg.cloneNode(true) as SVGSVGElement;
    img.replaceWith(svgNode);

    if (!parent) {
      return;
    }

    const existingMicrosoftText = parent.querySelector<HTMLSpanElement>(".mci-ms-logo-text");
    if (existingMicrosoftText) {
      existingMicrosoftText.textContent = "Microsoft";
      return;
    }

    const textSpan = document.createElement("span");
    textSpan.className = "mci-ms-logo-text";
    textSpan.textContent = "Microsoft";
    svgNode.insertAdjacentElement("afterend", textSpan);
  });

  return template.innerHTML;
}

function constructUrlWithLocale(apiUrl: string, locale: string): string {
  return apiUrl.replace(/en-us/gi, locale.toLowerCase());
}

async function getUHF(locale: string): Promise<XMLDocument | null> {
  const normalizedLocale = locale.toLowerCase();
  pendingUhfPromises[normalizedLocale] ??= (async (): Promise<XMLDocument | null> => {
    try {
      const url = constructUrlWithLocale(environment.uhfApiUrl, normalizedLocale);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`UHF fetch failed: ${response.statusText}`);
      }

      const rawContent = await response.text();
      const xmlDoc = new DOMParser().parseFromString(rawContent, "text/xml");
      if (xmlDoc.querySelector("parsererror")) {
        throw new Error("Failed to parse the UHF XML response");
      }

      assetsPromise ??= installUhfAssets(xmlDoc).catch((error: unknown) => {
        assetsPromise = undefined;
        throw error;
      });
      await assetsPromise;
      return xmlDoc;
    } catch (error) {
      console.error("Failed to fetch UHF:", error);
      delete pendingUhfPromises[normalizedLocale];
      return null;
    }
  })();

  return pendingUhfPromises[normalizedLocale];
}

function getUhfFragment(
  xmlDoc: XMLDocument,
  fragmentName: "headerHtml" | "footerHtml"
): string | null {
  return xmlDoc.getElementsByTagName(fragmentName)[0]?.textContent?.trim() ?? null;
}

async function installUhfAssets(xmlDoc: XMLDocument): Promise<void> {
  const cssMarkup = xmlDoc.getElementsByTagName("cssIncludes")[0]?.textContent;
  if (cssMarkup) {
    const template = document.createElement("template");
    template.innerHTML = cssMarkup;

    for (const sourceNode of Array.from(template.content.childNodes)) {
      if (!(sourceNode instanceof HTMLLinkElement) || sourceNode.rel !== "stylesheet") {
        document.head.appendChild(sourceNode);
        continue;
      }

      await new Promise<void>((resolve, reject) => {
        sourceNode.addEventListener("load", (): void => resolve(), { once: true });
        sourceNode.addEventListener(
          "error",
          (): void => reject(new Error(`Failed to load UHF stylesheet: ${sourceNode.href}`)),
          { once: true }
        );
        document.head.appendChild(sourceNode);
      });
    }
  }

  const javascriptMarkup = xmlDoc.getElementsByTagName("javascriptIncludes")[0]?.textContent;
  if (!javascriptMarkup) {
    return;
  }

  const template = document.createElement("template");
  template.innerHTML = javascriptMarkup;

  for (const sourceScript of Array.from(template.content.querySelectorAll("script"))) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      for (const attribute of Array.from(sourceScript.attributes)) {
        script.setAttribute(attribute.name, attribute.value);
      }
      script.textContent = sourceScript.textContent;
      script.addEventListener("load", (): void => resolve(), { once: true });
      script.addEventListener(
        "error",
        (): void => reject(new Error(`Failed to load UHF script: ${script.src}`)),
        { once: true }
      );
      document.head.appendChild(script);
      if (!script.src) {
        resolve();
      }
    });
  }

  const customElementNames = new Set<string>();
  for (const fragmentName of ["headerHtml", "footerHtml"] as const) {
    const fragmentMarkup = xmlDoc.getElementsByTagName(fragmentName)[0]?.textContent ?? "";
    const template = document.createElement("template");
    template.innerHTML = fragmentMarkup;

    template.content.querySelectorAll("*").forEach((element) => {
      if (element.localName.startsWith("uhf-")) {
        customElementNames.add(element.localName);
      }
    });
  }

  await Promise.all(
    Array.from(customElementNames, (elementName) => customElements.whenDefined(elementName))
  );
}

export async function getHeader(locale: string = "en-us"): Promise<string | null> {
  const xmlDoc = await getUHF(locale);
  return xmlDoc ? getUhfFragment(xmlDoc, "headerHtml") : null;
}

export async function getFooter(locale: string = "en-us"): Promise<string | null> {
  const xmlDoc = await getUHF(locale);
  return xmlDoc ? getUhfFragment(xmlDoc, "footerHtml") : null;
}

export async function getSupportedLocales(): Promise<ILanguage[]> {
  try {
    const response = await fetch(environment.uhfLocalePickersUrl);

    if (!response.ok) {
      throw new Error(`Locale pickers fetch failed: ${response.statusText}`);
    }

    const json = (await response.json()) as { locales?: ILanguage[] };

    return Array.isArray(json?.locales) ? json.locales : [];
  } catch (error) {
    console.error("Failed to fetch supported locales:", error);
    return [];
  }
}

export function clearUHFCache(): void {
  Object.keys(pendingUhfPromises).forEach((key) => delete pendingUhfPromises[key]);
}
